import { TrackedEvents, trackEvent } from 'analytics';
import type { HDPEComposition, QualityReport } from 'api';
import { formatDate } from 'date';
import { Trans, useTranslation } from 'i18n';
import { useNavigate } from 'react-router-dom';
import { DataCard, ExpandableIconButton, Typography } from 'ui';
import { ImageOutlined, Insights } from 'ui/icons';

import { HDPECompositionBreakdown } from '../../../breakdowns/components/HDPECompositionBreakdown/HDPECompositionBreakdown';
import { NoAnalysisAlert } from '../../../breakdowns/components/NoAnalysisAlert/NoAnalysisAlert';

export const HDPECompositionQualityReportDataCard = ({
  isLoading,
  balesAnalysed,
  latestInspection,
  composition,
  hasMedia,
}: {
  isLoading?: boolean;
  balesAnalysed: number;
  latestInspection: QualityReport['latest_inspection'];
  composition: HDPEComposition | null;
  hasMedia: boolean;
}) => {
  const { t } = useTranslation('features');
  const navigate = useNavigate();

  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'AI Bale Analysis',
    });
    navigate('media?tab=accepted');
  };

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          action: hasMedia ? (
            <ExpandableIconButton
              icon={<ImageOutlined />}
              label={t('dataCards.hdpeCompositionQualityReport.actions.showMedia')}
              onClick={handleClickMedia}
            />
          ) : undefined,
          avatar: <Insights />,
          title: t('dataCards.hdpeCompositionQualityReport.title'),
        },
      }}
    >
      <DataCard.Item colSpan={12}>
        <Typography
          variant="body1"
          color="secondary"
          sx={{
            paddingBottom: 2,
          }}
        >
          <Trans
            i18nKey="dataCards.hdpeCompositionQualityReport.description"
            namespace="features"
            components={{
              bold: <b />,
            }}
          />
        </Typography>
      </DataCard.Item>
      <DataCard.Item title={t('dataCards.hdpeCompositionQualityReport.fields.supplierBalesAnalysed')} colSpan={4}>
        {balesAnalysed}
      </DataCard.Item>
      {latestInspection ? (
        <DataCard.Item title={t('dataCards.hdpeCompositionQualityReport.fields.latestInspection')} colSpan={8}>
          {formatDate(latestInspection)}
        </DataCard.Item>
      ) : null}
      <DataCard.Item colSpan={12}>
        {composition ? (
          <HDPECompositionBreakdown composition={composition} />
        ) : (
          <NoAnalysisAlert aiModel="hdpe" testId="hdpe-no-analysis-alert" />
        )}
      </DataCard.Item>
    </DataCard>
  );
};
